// src/components/filter.
import React, { useEffect, useMemo, useState } from "react";
import { OrderService } from "services/OrderService";
//import components
import Breadcrumbs from "../../components/Common/Breadcrumb";
import TableContainer from "../../components/Common/TableContainer";
import { Row, Col, CardBody, Card } from "reactstrap";
import { format } from "date-fns";
import { AuthData, useAuth } from "context/AuthProvider";
import { OrderResponse, PaymentStatus } from "models/orderResponse";
import { toast } from "react-toastify";
import { PaymentType } from "enum/PaymentOption";

const DraftOrders: React.FC = () => {
  const [pendingOrders, setPendingOrders] = useState([] as OrderResponse[]);
  const [user] = useAuth() as AuthData;
  const columns = useMemo(
    () => [
      {
        Header: "Order Id",
        accessor: "uniqueId",
        Cell: ({ value }: any) => {
          return <div className="link-primary">{value}</div>;
        },
      },
      {
        Header: <div style={{ whiteSpace: "pre-line", width: "150px" }}>Individual / Company Name</div>,
        accessor: user?.isABusiness ? "userId.address.companyName" : "userId.firstName",
      },
      {
        Header: "Date",
        accessor: "deliveryTime",
        Cell: ({ value }: any) => {
          return format(new Date(value), "dd.MM.yyyy");
        },
      },
      {
        Header: "Pickup",
        accessor: "pickUpAddress.city",
      },
      {
        Header: "PPC",
        accessor: "pickUpAddress.postalCode",
        Cell: ({ value }) => <div style={{ width: 80 }}>{value}</div>,
      },
      {
        Header: "Delivery",
        accessor: "deliveryAddress.city",
      },
      {
        Header: "DPC",
        accessor: "deliveryAddress.postalCode",
        Cell: ({ value }) => <div style={{ width: 80 }}>{value}</div>,
      },
      {
        Header: "Driver",
        accessor: "driverId.firstName driverId.lastName",
      },
      {
        Header: "Weight (lbs)",
        accessor: "totalWeight",
        Cell: props => {
          return props.value.replace(/ pounds/gi, "");
        },
      },
      {
        Header: "Vehicle Type",
        accessor: "vehicleType",
      },
      {
        Header: "Price",
        accessor: "totalFare",
        Cell: ({ value }) => `$${value.toFixed(2)}`,
      },
      {
        Header: "Order Time",
        accessor: "packageReadyTime",
        Cell: ({ row }: any) => {
          return format(new Date(row.original.packageReadyTime), "hh:mma") + " to " + format(new Date(row.original.deliveryTime), "hh:mma");
        },
      },
      {
        Header: "Status",
        accessor: "paymentStatus",
      },
      {
        id: "action",
        Header: "Actions",
        accessor: "_id",
        Cell: ({ row }: any) => {
          return (
            <td className="px-2">
              <div className="row ">
                <div className="col-md-10 text-center">
                  <button className="btn btn-success btn-sm" onClick={() => getStripeSession(row.original?._id)}>
                    Pay
                  </button>
                </div>
              </div>
            </td>
          );
        },
      },
    ],
    []
  );

  const LIMIT_P_C = 10;

  const [isOffset_p_c, setOffset_p_c] = useState<number>(1);
  const [seeMore_p_c, setSeeMore_p_c] = useState(false);

  const data = {
    paymentStatus: PaymentStatus.PENDING,
    uniqueId: "",
  };

  useEffect(() => {
    OrderService.getOrdersByPaymentStatus(data, LIMIT_P_C, isOffset_p_c).then(res => {
      if (res.success) {
        setPendingOrders(res.data);
        setSeeMore_p_c(!(res.data && res.data.length > 0 && res.data.length == LIMIT_P_C));
      } else {
        setSeeMore_p_c(true);
      }
    });
  }, []);

  const searchTable = (serchText: string) => {
    const searchdata = {
      paymentStatus: PaymentStatus.PENDING,
      uniqueId: "",
      searchableString: serchText,
    };
    const updatedSkip = isOffset_p_c + 1;
    setOffset_p_c(updatedSkip);
    OrderService.getOrdersByPaymentStatusBySearch(searchdata, LIMIT_P_C).then(res => {
      if (res.success) {
        if (res.data.length !== 0) {
          setPendingOrders(res.data);
          setSeeMore_p_c(!(res.data.length > 0 && res.data.length == LIMIT_P_C));
        }
      } else {
        setSeeMore_p_c(true);
      }
    });
  };

  const SeeMore = () => {
    const updatedSkip = isOffset_p_c + 1;
    setOffset_p_c(updatedSkip);
    OrderService.getOrdersByPaymentStatus(data, LIMIT_P_C, updatedSkip).then(res => {
      if (res.success) {
        setPendingOrders(prev => {
          return [...new Set([...prev, ...res.data])];
        });
        setSeeMore_p_c(!(res.data && res.data.length > 0 && res.data.length == LIMIT_P_C));
      } else {
        setSeeMore_p_c(true);
      }
    });
  };

  const getStripeSession = (id: string) => {
    const orderId = id;

    OrderService.createStripeSession({ orderIdList: [orderId], paymentType: PaymentType.ORDER })
      .then(res => {
        if (res.success) {
          // window.location.href = res.data;
          window.open(res.data, '_blank');
        } else {
          return toast.error(res.error, {
            position: toast.POSITION.BOTTOM_RIGHT,
            className: "foo-bar",
            style: { marginBottom: "4rem" },
          });
        }
      })
      .catch(err => {
        console.error(err.message);
      });
  };

  //meta title
  document.title = "Delivered Order | Zulu Courier ";

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Search" breadcrumbItem="Delivered Orders" />
        <Col lg={12}>
          <Card>
            <CardBody>
              {(pendingOrders && pendingOrders.length) > 0 ? (
                <TableContainer
                  columns={columns}
                  data={pendingOrders}
                  currentPage={undefined}
                  pagelimit={undefined}
                  handlePageChange={undefined}
                  handleColumnChange={undefined}
                  selectedColumns={undefined}
                  showColumnChange={false}
                  handleSearch={searchTable}
                  isGlobalFilter={true}
                  isAddOptions={false}
                  customPageSize={100}
                  className="custom-header-css"
                  isJobListGlobalFilter={undefined}
                  isAddUserList={undefined}
                  handleOrderClicks={undefined}
                  handleUserClick={undefined}
                  handleCustomerClick={undefined}
                  isAddCustList={undefined}
                  handleSendEmail={undefined}
                  isPDFGenerate={false}
                  ispopup={true}
                  isAddressBook={false}
                  hancleDeleteRecords={undefined}
                  handleSelect={undefined}
                  selectedID={undefined}
                  hasCheckBox={false} footerData={undefined} />
              ) : (
                <Row className="justify-content-md-center justify-content-center align-items-center">
                  <Col className="col-md-auto">
                    <div>No Delivered Orders</div>
                  </Col>
                </Row>
              )}
              {(pendingOrders && pendingOrders.length) > 9 && (
                <Row className="justify-content-md-start">
                  <Col className="col-md-auto">
                    <div className="d-flex gap-1">
                      <button className="btn btn-primary btn-sm px-4 me-lg-2 me-md-2 mb-2" disabled={seeMore_p_c} onClick={() => SeeMore()}>
                        See More
                      </button>
                    </div>
                  </Col>
                </Row>
              )}
            </CardBody>
          </Card>
        </Col>
      </div>
    </div>
  );
};

export default DraftOrders;
